import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import type { AssessmentFormValues, TaskType } from "@/core/types";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { showToast } from "@/providers/ToastProvider";
import type { ToastConfig } from "@/ui2/molecules/Toast";
import { tryCatch } from "@/lib/helpers/tryCatch";
import { useTaskIdByType } from "@/lib/hooks/useTaskIdByType";

import { useAssessmentDefinition } from "../api/queries/useAssessmentDefinition";
import { useSubmitAssessment } from "../api/mutations/useSubmitAssessment";

import { AssessmentError } from "./AssessmentError";
import { AssessmentRenderer } from "./AssessmentRenderer";
import { AssessmentSkeleton } from "./AssessmentSkeleton";

type AssessmentContainerProps = {
  definitionId: string;
  onSuccessCallback: () => void;
  taskType: TaskType;
  toastConfig: ToastConfig;
};

export function AssessmentContainer({
  definitionId,
  onSuccessCallback,
  taskType,
  toastConfig,
}: AssessmentContainerProps) {
  const history = useHistory();
  const taskId = useTaskIdByType(taskType);
  const {
    data: assessment,
    error,
    isLoading,
  } = useAssessmentDefinition({
    id: definitionId,
  });

  const { isPending: isMutating, mutateAsync: submitAssessmentAnswers } = useSubmitAssessment({
    onSuccessCallback,
    taskId,
    toastConfig,
  });

  useEffect(() => {
    // User should not submit assessment without valid taskId
    if (!taskId) history.push("/app/home");
    // remove taskId from deps bc task is removed from redux on submit
    // and this effect would run and interrupt the onSucess flow
  }, [history]);

  if (isLoading) return <AssessmentSkeleton />;

  if (error || !assessment) {
    return <AssessmentError definitionId={definitionId} error={error} />;
  }

  const handleSubmit = async (formData: AssessmentFormValues) => {
    const [, error] = await tryCatch(
      submitAssessmentAnswers({
        assessmentDefinitionId: assessment.id,
        assessmentDefinitionVersion: assessment.version,
        formData,
        taskId,
      }),
    );

    if (error) {
      captureErrorMessage(parseError({ error, fallback: "Assessment submission error" }), {
        tags: {
          assessmentId: assessment.id,
          assessmentName: assessment.name,
          component: "AssessmentContainer",
          errorType: "assessment_submission",
        },
      });

      showToast([
        {
          heading: "Something went wrong",
          text: "Please try submitting again.",
          variant: "error",
        },
      ]);
    }
  };

  return (
    <AssessmentRenderer assessment={assessment} isSubmitting={isMutating} onSubmit={handleSubmit} />
  );
}
